// Kopie aus aivy-customer-dashboard, nicht von Hand ändern.
// Quelle ist src/features/pricing/usage/careers.js, der Stand steht in
// UPSTREAM.json daneben. Angepasst sind nur die Importpfade. Eine neue
// Regel kommt zuerst ins Dashboard, danach wird diese Datei neu kopiert und
// die Checksumme in UPSTREAM.json nachgezogen. upstream.test.js prüft das.

// Zählregel für gleichzeitig aktive Stellen (Paket-Limit `careers`). Reine
// Funktionen, damit Jest sie ohne helpers/index.js testen kann. sample-data.js
// importiert selbst nichts und ist deshalb sicher. Aivy OS kopiert die Vektoren
// aus __fixtures__/count-cases.json, Änderungen an der Regel gehören in beide
// Repos.

import { isGeneratedSampleCareer } from '../sample-data'

export const CAREER_SLOT_ACTIONS = {
  ACTIVATE: 'activate',
  RECOVER: 'recover'
}

const VERSION_SUFFIX = /-v\d+$/

const toList = (value) => {
  if (Array.isArray(value)) return value
  return value ? [value] : []
}

// Die Produkt-Tour legt eine Stelle `PRODUCT_TOUR-<id>` an. Gleiche Prüfung
// wie in hooks/use-talents-query.js und pages/careers.js.
export const isProductTourCareer = (career) =>
  String(career?.id).includes('PRODUCT_TOUR')

// careerControl.activateCareer (aivy-backend-app, sharedCode/careerControl)
// verschiebt beim erneuten Aktivieren derselben external_custom_id die alte
// Version nach `<ext>-p-<partner_id>-v<n>` mit external_custom_id `<ext>-v<n>`.
// Die Altversion bleibt ACTIVE, ist aber keine eigene Stelle. Geprüft wird das
// exakte Paar, eine Stelle, deren ID nur zufällig auf -v2 endet, zählt weiter.
// In enforced blendet der Einladen-Dialog (create-space-modal.js) sie aus,
// sonst bleibt sie wie heute wählbar (README, Bekannte Randfälle).
export const isVersionedCareerCopy = (career) => {
  const { id, partner_id, external_custom_id } = career || {}
  if (typeof id !== 'string' || !partner_id || external_custom_id == null) {
    return false
  }

  const ext = String(external_custom_id)
  const match = VERSION_SUFFIX.exec(ext)
  if (!match) return false

  const base = ext.slice(0, -match[0].length)

  return !!base && id === `${base}-p-${partner_id}${match[0]}`
}

// Entwürfe (WAIT_*) und archivierte Stellen zählen nicht. Generierte
// Beispielstellen auch nicht. Discover-Vorlagen mit is_demo zählen dagegen mit,
// weil man zu ihnen einladen kann (deshalb isGeneratedSampleCareer und nicht
// isSampleCareer).
export const countsAsActiveCareer = (career) =>
  !!career &&
  career.status === 'ACTIVE' &&
  career.archived !== true &&
  !isProductTourCareer(career) &&
  !isGeneratedSampleCareer(career) &&
  !isVersionedCareerCopy(career)

export const countActiveCareers = (careers) =>
  toList(careers).filter(countsAsActiveCareer).length

const afterAction = (action, career) => {
  if (action === CAREER_SLOT_ACTIONS.ACTIVATE) {
    return { ...career, status: 'ACTIVE' }
  }
  if (action === CAREER_SLOT_ACTIONS.RECOVER) {
    return { ...career, archived: false }
  }

  return null
}

// Die ID, die activateCareer bei gesetzter external_custom_id belegt. Ist sie
// schon aktiv, ersetzt die neue Stelle die alte, und der Slot bleibt gleich.
const activationSlotId = (career) => {
  if (career?.external_custom_id == null || !career.partner_id) return null

  return `${career.external_custom_id}-p-${career.partner_id}`
}

const replacedCareerId = (action, career) =>
  action === CAREER_SLOT_ACTIONS.ACTIVATE ? activationSlotId(career) : null

/**
 * Ob das Aktivieren von career eine gezählte Stelle ersetzt und deshalb
 * keinen Platz kostet. Dieselbe Regel wie in careerSlotDelta, für Hinweise im
 * Erstell-Flow. Eine archivierte Stelle, ein Entwurf, die Tour oder eine
 * Beispielstelle mit derselben ID ersetzt nichts, dann kostet es einen Platz.
 *
 * @param {{ career: { external_custom_id?: string|null, partner_id?: string },
 *   careers: object[] }} params careers sind die bekannten Stellen des Partners
 * @returns {boolean}
 */
export const replacesCountedCareer = ({ career, careers } = {}) => {
  const slotId = activationSlotId(career)
  if (!slotId) return false

  return toList(careers).some(
    (other) => other?.id === slotId && countsAsActiveCareer(other)
  )
}

// Wie viele Slots eine Aktion zusätzlich belegt. 0 heißt, die Stelle zählt
// schon, wird nie gezählt (Beispiel, Tour, Altversion), bleibt nach dem
// Wiederherstellen ein Entwurf oder ersetzt eine aktive Stelle mit derselben
// external_custom_id. Sonst 1 je Stelle, mehrere Stellen werden summiert.
// Eine unbekannte Aktion ergibt 0, das Gate sperrt dann nicht (fail-open).
export const careerSlotDelta = ({ action, careers, activeCareers } = {}) => {
  const counted = new Set(
    toList(activeCareers)
      .filter(countsAsActiveCareer)
      .map(({ id }) => id)
  )

  return toList(careers).reduce((delta, career) => {
    if (!career) return delta
    if (career.id != null && counted.has(career.id)) return delta
    if (countsAsActiveCareer(career)) return delta

    const next = afterAction(action, career)
    if (!next || !countsAsActiveCareer(next)) return delta

    const replacedId = replacedCareerId(action, career)
    if (replacedId && counted.has(replacedId)) return delta

    const slotId = replacedId || career.id
    if (slotId != null) counted.add(slotId)

    return delta + 1
  }, 0)
}
